import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AtomicVariablesContext = createContext();

// Storage key prefix
const STORAGE_KEY_PREFIX = 'art_vars_';

export const AtomicVariablesProvider = ({ children, projectId }) => {
  const [variables, setVariables] = useState({});
  const [currentProjectId, setCurrentProjectId] = useState(projectId);

  // Get storage key for current project
  const getStorageKey = useCallback((pid) => {
    return `${STORAGE_KEY_PREFIX}${pid || 'global'}`;
  }, []);

  // Load variables from localStorage when project changes
  useEffect(() => {
    if (projectId !== currentProjectId) {
      setCurrentProjectId(projectId);
    }

    const key = getStorageKey(projectId);
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        const parsed = JSON.parse(stored);
        setVariables(parsed);
      } else {
        setVariables({});
      }
    } catch (error) {
      console.error('Error loading ART variables:', error);
      setVariables({});
    }
  }, [projectId, currentProjectId, getStorageKey]);

  // Save variables to localStorage whenever they change
  useEffect(() => {
    const key = getStorageKey(currentProjectId);
    try {
      localStorage.setItem(key, JSON.stringify(variables));
    } catch (error) {
      console.error('Error saving ART variables:', error);
    }
  }, [variables, currentProjectId, getStorageKey]);

  // Get a single variable value
  const getVariable = useCallback((name, defaultValue = '') => {
    return variables[name] !== undefined ? variables[name] : defaultValue;
  }, [variables]);

  // Set a single variable value
  const setVariable = useCallback((name, value) => {
    setVariables(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  // Set multiple variables at once
  const setMultipleVariables = useCallback((varsObj) => {
    setVariables(prev => ({
      ...prev,
      ...varsObj
    }));
  }, []);

  // Get all variables
  const getAllVariables = useCallback(() => {
    return { ...variables };
  }, [variables]);

  // Clear all variables for current project
  const clearVariables = useCallback(() => {
    setVariables({});
  }, []);

  // Clear a specific variable
  const removeVariable = useCallback((name) => {
    setVariables(prev => {
      const newVars = { ...prev };
      delete newVars[name];
      return newVars;
    });
  }, []);

  // Get variables with defaults merged from input arguments
  const getVariablesWithDefaults = useCallback((inputArguments = []) => {
    const result = {};
    inputArguments.forEach(arg => {
      const storedValue = variables[arg.name];
      result[arg.name] = storedValue !== undefined ? storedValue : (arg.default || '');
    });
    return result;
  }, [variables]);

  // Check if a variable has been customized (differs from default)
  const isVariableCustomized = useCallback((name, defaultValue) => {
    return variables[name] !== undefined && variables[name] !== defaultValue;
  }, [variables]);

  // Get count of stored variables
  const getVariableCount = useCallback(() => {
    return Object.keys(variables).length;
  }, [variables]);

  // Export variables as JSON
  const exportVariables = useCallback(() => {
    return JSON.stringify(variables, null, 2);
  }, [variables]);

  // Import variables from JSON
  const importVariables = useCallback((jsonString, merge = true) => {
    try {
      const imported = JSON.parse(jsonString);
      if (merge) {
        setVariables(prev => ({
          ...prev,
          ...imported
        }));
      } else {
        setVariables(imported);
      }
      return true;
    } catch (error) {
      console.error('Error importing ART variables:', error);
      return false;
    }
  }, []);

  const value = {
    variables,
    getVariable,
    setVariable,
    setMultipleVariables,
    getAllVariables,
    clearVariables,
    removeVariable,
    getVariablesWithDefaults,
    isVariableCustomized,
    getVariableCount,
    exportVariables,
    importVariables,
    projectId: currentProjectId
  };

  return (
    <AtomicVariablesContext.Provider value={value}>
      {children}
    </AtomicVariablesContext.Provider>
  );
};

// Hook to use the atomic variables context
export const useAtomicVariables = () => {
  const context = useContext(AtomicVariablesContext);
  if (!context) {
    throw new Error('useAtomicVariables must be used within an AtomicVariablesProvider');
  }
  return context;
};

// Higher-order component for class components
export const withAtomicVariables = (Component) => {
  return function WrappedComponent(props) {
    const atomicVariables = useAtomicVariables();
    return <Component {...props} atomicVariables={atomicVariables} />;
  };
};

export default AtomicVariablesContext;
